import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, PageBreak, Table, TableRow, TableCell, WidthType, ImageRun } from 'docx';
import cfgLogo from '@/assets/cfg-logo.png';

// Load logo image
const loadLogoImage = async (): Promise<Buffer | null> => {
  try {
    const response = await fetch(cfgLogo);
    const blob = await response.blob();
    const arrayBuffer = await blob.arrayBuffer();
    return Buffer.from(arrayBuffer);
  } catch (error) {
    console.error('Error loading logo:', error);
    return null;
  }
};

interface AgreementData {
  primarySellerName: string;
  secondarySellerName?: string;
  propertyAddress: string;
  balance: string;
  firstPaymentDate: string;
  dateOfBalance: string;
  date: Date;
  agreementType: "partial" | "full" | "private";
  paperType?: string;
  partialDollarAmount?: string;
  brokerOfferToSeller?: string;
  partialEndDate?: string;
  // Private investor specific fields
  buyerName?: string;
  purchaserName?: string;
  contractDate?: string;
  interestRate?: string;
  nextPaymentDue?: string;
  purchaseAmount?: string;
  liquidatedDamages?: string;
  draftedBy?: string;
  referenceNumber?: string;
}

export const generateWordDocument = async (agreementData: AgreementData, options?: { agreementOnly?: boolean }) => {
  const sellerNames = agreementData.secondarySellerName 
    ? `${agreementData.primarySellerName} and ${agreementData.secondarySellerName}`
    : agreementData.primarySellerName;

  const formatCurrency = (amount: string) => {
    const num = parseFloat(amount || "0");
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(num);
  };

  // Load logo
  const logoImage = await loadLogoImage();
  
  // Cover Letter Content  
  const coverLetterContent = [
    // Header (no logo for now to avoid type issues)
    new Paragraph({
      children: [
        new TextRun({
          text: "CONTRACT FUNDING GROUP, LLC",
          bold: true,
          size: 32,
          color: "8B4513"
        })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "Professional Note Purchasing Services",
          bold: true,
          size: 24,
          color: "FF6B35"
        })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 }
    }),

    // Date
    new Paragraph({
      children: [
        new TextRun({
          text: agreementData.date ? agreementData.date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          }) : "",
          size: 20
        })
      ],
      alignment: AlignmentType.RIGHT,
      spacing: { after: 300 }
    }),

    // Recipient
    new Paragraph({
      children: [
        new TextRun({
          text: agreementData.primarySellerName,
          bold: true,
          size: 22
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "Land Contract Holder",
          size: 20,
          color: "666666"
        })
      ],
      spacing: { after: 200 }
    }),

    // Subject
    new Paragraph({
      children: [
        new TextRun({
          text: "RE: Purchase Proposal",
          bold: true,
          size: 22
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `Property: ${agreementData.propertyAddress}`,
          size: 20,
          color: "666666"
        })
      ],
      spacing: { after: 300 }
    }),

    // Body
    new Paragraph({
      children: [
        new TextRun({
          text: `Dear ${agreementData.primarySellerName},`,
          size: 22
        })
      ],
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "We are pleased to present our purchase proposal.",
          size: 22
        })
      ],
      spacing: { after: 300 }
    }),

    // Proposal Summary
    new Paragraph({
      children: [
        new TextRun({
          text: "Purchase Proposal",
          bold: true,
          size: 22,
          color: "8B4513"
        })
      ],
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `Property: ${agreementData.propertyAddress}`,
          size: 20
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `Balance: ${formatCurrency(agreementData.balance)} • Offer: ${formatCurrency(agreementData.brokerOfferToSeller || "0")}`,
          size: 20
        })
      ],
      spacing: { after: 100 }
    }),
    ...(agreementData.agreementType === "partial" && agreementData.partialDollarAmount ? [
      new Paragraph({
        children: [
          new TextRun({
            text: `Partial: ${formatCurrency(agreementData.partialDollarAmount)}`,
            size: 20
          })
        ],
        spacing: { after: 100 }
      })
    ] : []),
    new Paragraph({
      children: [
        new TextRun({
          text: `Type: ${agreementData.agreementType === "partial" ? "Partial" : agreementData.agreementType === "private" ? "Private Investor" : "Full"} Purchase`,
          size: 20
        })
      ],
      spacing: { after: 300 }
    }),

    // Process
    new Paragraph({
      children: [
        new TextRun({
          text: "Process (30-45 days)",
          bold: true,
          size: 22,
          color: "8B4513"
        })
      ],
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "Due diligence → Title/payment verification → Closing/payment",
          size: 20
        })
      ],
      spacing: { after: 300 }
    }),

    // Required Documents
    new Paragraph({
      children: [
        new TextRun({
          text: "Required Documents",
          bold: true,
          size: 22,
          color: "8B4513"
        })
      ],
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "Original contract • Payment history • Tax records • Insurance • Borrower contacts",
          size: 20
        })
      ],
      spacing: { after: 300 }
    }),

    // Conclusion
    new Paragraph({
      children: [
        new TextRun({
          text: "The attached Purchase Agreement outlines terms. This 30-day offer requires signed agreement.",
          size: 20
        })
      ],
      spacing: { after: 400 }
    }),

    // Signature
    new Paragraph({
      children: [
        new TextRun({
          text: "Sincerely,",
          size: 20
        })
      ],
      spacing: { after: 600 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "Contract Funding Group, LLC",
          size: 20
        })
      ],
      spacing: { after: 400 }
    }),

    // Footer
    new Paragraph({
      children: [
        new TextRun({
          text: "Contract Funding Group, LLC • info@contractfundinggroup.com",
          size: 18,
          color: "666666"
        })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { before: 400 }
    })
  ];

  // Agreement Content
  const agreementContent = agreementData.agreementType === "partial" 
    ? generatePartialAgreementContent(agreementData, sellerNames)
    : agreementData.agreementType === "private"
    ? generatePrivateInvestorAgreementContent(agreementData, sellerNames)
    : generateFullAgreementContent(agreementData, sellerNames);

  // Build logo paragraph for agreement-only mode
  const logoHeader: Paragraph[] = [];
  if (options?.agreementOnly && logoImage) {
    logoHeader.push(
      new Paragraph({
        children: [
          new ImageRun({
            data: logoImage,
            transformation: { width: 150, height: 180 },
            type: 'png',
          }),
        ],
        alignment: AlignmentType.CENTER,
        spacing: { after: 300 }
      })
    );
  }

  let sections;
  if (options?.agreementOnly) {
    // Agreement only with logo, no cover letter
    sections = [{ children: [...logoHeader, ...agreementContent] }];
  } else if (agreementData.agreementType === "private") {
    // Private investor: no cover letter
    sections = [{ children: agreementContent }];
  } else {
    // Full package: cover letter + agreement
    sections = [
      { children: coverLetterContent },
      { children: agreementContent }
    ];
  }

  const doc = new Document({
    sections: sections
  });

  return await Packer.toBlob(doc);
};

const generatePrivateInvestorAgreementContent = (agreementData: AgreementData, sellerNames: string) => {
  const balanceDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  
  return [
    new Paragraph({
      children: [
        new TextRun({
          text: "PURCHASE & SALE AGREEMENT",
          bold: true,
          size: 22
        })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "MI LAND CONTRACT FULL BUYOUT",
          bold: true,
          size: 18
        })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `This Agreement is executed between ${sellerNames} ("Seller" or "You") whose mailing address is ________________, and ${agreementData.buyerName || "Contract Funding Group, LLC"}, ("Buyer" or "Us"), whose business address is ________________.`,
          size: 20
        })
      ],
      spacing: { after: 300 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "COVENANTS",
          bold: true,
          size: 22
        })
      ],
      spacing: { before: 200, after: 200 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "Buyer and Seller mutually agree and promise to the following:",
          size: 20
        })
      ],
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "1. The subject Land Contract (the term \"Land Contract\" for the purposes of this Agreement shall include any note or lien integrated with the Land Contract) is identified as:",
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `   The purchaser under the Land Contract is ${agreementData.purchaserName || "________________"} ("Purchaser"),`,
          size: 20
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `   Dated ${agreementData.contractDate || "________________"},`,
          size: 20
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `   Having the current balance of $${agreementData.balance || "________________"} as of ${balanceDate},`,
          size: 20
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `   Interest of ${agreementData.interestRate || "________________"}%,`,
          size: 20
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `   Secured by property located at ${agreementData.propertyAddress || "________________"}`,
          size: 20
        })
      ],
      spacing: { after: 250 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `2. Promise to Buy/Sell. `,
          bold: true,
          size: 20
        }),
        new TextRun({
          text: `Seller promises to sell and Buyer promises to buy all of the remaining payments under the Land Contract, beginning with the payment due on ${agreementData.nextPaymentDue || "________________"}. Payable as follows: $${agreementData.purchaseAmount || "________________"} at closing, as set forth in the final closing statement.`,
          size: 20
        })
      ],
      spacing: { after: 250 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "3. Warranties. ",
          bold: true,
          size: 20
        }),
        new TextRun({
          text: "The Seller makes the following representations and warranties as an inducement for the Buyer to complete the sale of the Land Contract:",
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "   a) All information in this Agreement is correct",
          size: 20
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "   b) Seller holds all right, title and interest and can convey clear title to the Purchaser in the Land Contract.",
          size: 20
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "   c) The Land Contract represents a bona fide sale of real estate. The Land Contract was binding on the date written on the face.",
          size: 20
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "   d) Seller has no knowledge or notice of any defenses or offsets to the full and complete payment of the Land Contract.",
          size: 20
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "   e) The Land Contract does not violate any applicable Federal or state truth-in-lending, consumer protection or usury laws.",
          size: 20
        })
      ],
      spacing: { after: 250 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "4. Seller's Cancellation. ",
          bold: true,
          size: 20
        }),
        new TextRun({
          text: `Should the Seller elect to cancel this Agreement and/or should Seller not comply with standard closing procedures, Seller shall be liable for $${agreementData.liquidatedDamages || "500.00"} as liquidated damages.`,
          size: 20
        })
      ],
      spacing: { after: 250 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "5. Buyer's Right to Cancel. ",
          bold: true,
          size: 20
        }),
        new TextRun({
          text: "Buyer has the right to cancel this Agreement should it deem, in its sole and absolute discretion, any one of the following insufficient: Purchaser's Credit, payment history, title problems, or adverse property appraisal.",
          size: 20
        })
      ],
      spacing: { after: 250 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "6. Modification. ",
          bold: true,
          size: 20
        }),
        new TextRun({
          text: "This Agreement represents the entire contract between Buyer and Seller. This Agreement may only be amended by a written document, signed by the authorized representative of Buyer.",
          size: 20
        })
      ],
      spacing: { after: 250 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "7. Time of the Essence. ",
          bold: true,
          size: 20
        }),
        new TextRun({
          text: "Time is of the essence in respect to this Agreement. The Seller specifically acknowledges and agrees that this agreement is not a loan to you, and has not been characterized by us as a loan. Rather, it is the purchase of a stream of payments that are due to you from the Purchaser described above.",
          size: 20
        })
      ],
      spacing: { after: 400 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "Signed by: _______________________________",
          size: 20
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "Date: _______________________________",
          size: 20
        })
      ],
      spacing: { after: 300 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "SELLER:                                          BUYER:",
          bold: true,
          size: 18
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "______________________________________    ______________________________________",
          size: 18
        })
      ],
      spacing: { after: 50 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `${agreementData.primarySellerName || "Seller Name"}                                        ${agreementData.buyerName || "Contract Funding Group, LLC"}`,
          size: 18
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "Date: _______________                           Date: _______________",
          size: 18
        })
      ],
      spacing: { after: 100 }
    }),
    ...(agreementData.secondarySellerName ? [
      new Paragraph({
        children: [
          new TextRun({
            text: "______________________________________",
            size: 18
          })
        ],
        spacing: { after: 50 }
      }),
      new Paragraph({
        children: [
          new TextRun({
            text: agreementData.secondarySellerName,
            size: 18
          })
        ],
        spacing: { after: 50 }
      }),
      new Paragraph({
        children: [
          new TextRun({
            text: "Date: _______________",
            size: 18
          })
        ],
        spacing: { after: 100 }
      })
    ] : []),
    new Paragraph({
      children: [
        new TextRun({
          text: `Drafted By: ${agreementData.draftedBy || "________________"} | Reference: ${agreementData.referenceNumber || "________________"}`,
          size: 16
        })
      ]
    })
  ];
};

const generatePartialAgreementContent = (agreementData: AgreementData, sellerNames: string) => {
  return [
    new Paragraph({
      children: [
        new TextRun({
          text: "Commitment to Purchase Agreement",
          bold: true,
          size: 24
        })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 300 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `This Purchase Agreement made and entered into ${agreementData.date ? agreementData.date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : "________________"},`,
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `By and between, ${sellerNames} and Contract Funding Group, LLC and/or assigns, whereas SELLER is the owner of a certain ${agreementData.paperType || "________________"}.`,
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `The property address is: ${agreementData.propertyAddress || "________________"}`,
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `SELLER agrees to transfer said ${agreementData.paperType || "________________"}, all upon the following Terms and Conditions as set forth herein: Now therefore, for and in consideration of the value to be paid, mutual benefits derived, the SELLER agrees to grant the PURCHASER and/or assigns the exclusive right to purchaser the note and security instrument, and the parties hereto agree to as follows:`,
          size: 20
        })
      ],
      spacing: { after: 250 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `1. PURCHASE PRICE: SELLER warrants and represents that the balance due on the above mentioned ${agreementData.paperType || "________________"} as of ${agreementData.dateOfBalance || "________________"} is $${agreementData.balance || "________________"}.`,
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `Purchaser agrees to buy the next $${agreementData.partialDollarAmount || "________________"} due and you agree to sell the above described sum for $${agreementData.brokerOfferToSeller || "________________"} with payments beginning to PURCHASER on ${agreementData.firstPaymentDate || "________________"} and ending with the payment due ${agreementData.partialEndDate || "________________"} assuming there are no unpaid fees or costs accumulated by the borrowers.`,
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "2. COSTS: PURCHASER will pay title and appraisal costs",
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "3. SELLER warrants that all real property taxes that are due and payable have been paid.",
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "4. CONTINGENT: This purchase shall be expressly contingent upon the following Conditions:",
          size: 20
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `A. The real property securing the above described ${agreementData.paperType || "________________"} shall be of sufficient value to secure the investment. The determination of sufficient value shall be the sole discretion of PURCHASER.`,
          size: 20
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `B. PURCHASER shall have 30 days to fully examine the SELLER'S title, the ${agreementData.paperType || "________________"}, the CONTRACTED property, payment history, credit and financial status of the payers.`,
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "5. If any of the above mentioned conditions of sale are found to be unacceptable to PURCHASER, the PURCHASER shall notify SELLER and this contract shall become null and void.",
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `6. SELLER shall keep and maintain documentation of any ${agreementData.paperType || "________________"} payments that are paid during the processing of this transaction.`,
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "7. This agreement shall be in full force and effect for a period of 30 days from the date of the PURCHASER receiving all of the documentation requested.",
          size: 20
        })
      ],
      spacing: { after: 400 }
    }),
    
    // Signature lines
    new Paragraph({
      children: [
        new TextRun({
          text: "______________________________________                    ______________________________________",
          size: 20
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `${agreementData.primarySellerName || "Primary Seller"}                                        Contract Funding Group, LLC`,
          size: 20
        })
      ],
      spacing: { after: 200 }
    }),
    ...(agreementData.secondarySellerName ? [
      new Paragraph({
        children: [
          new TextRun({
            text: "______________________________________",
            size: 20
          })
        ],
        spacing: { after: 100 }
      }),
      new Paragraph({
        children: [
          new TextRun({
            text: agreementData.secondarySellerName,
            size: 20
          })
        ],
        spacing: { after: 200 }
      })
    ] : [])
  ];
};

const generateFullAgreementContent = (agreementData: AgreementData, sellerNames: string) => {
  return [
    new Paragraph({
      children: [
        new TextRun({
          text: "Commitment to Purchase Agreement",
          bold: true,
          size: 24
        })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 300 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `This Purchase Agreement made and entered into ${agreementData.date ? agreementData.date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : "________________"},`,
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `By and between, ${sellerNames} and Contract Funding Group, LLC and/or assigns, whereas SELLER is the owner of a certain ${agreementData.paperType || "________________"}.`,
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `The property address is: ${agreementData.propertyAddress || "________________"}`,
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `SELLER agrees to transfer said ${agreementData.paperType || "________________"}, all upon the following Terms and Conditions as set forth herein: Now therefore, for and in consideration of the value to be paid, mutual benefits derived, the SELLER agrees to grant the PURCHASER and/or assigns the exclusive right to purchaser the note and security instrument, and the parties hereto agree to as follows:`,
          size: 20
        })
      ],
      spacing: { after: 250 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `1. PURCHASE PRICE: SELLER warrants and represents that the balance due on the above mentioned ${agreementData.paperType || "________________"} as of ${agreementData.dateOfBalance || "________________"} is $${agreementData.balance || "________________"}.`,
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `2. PURCHASER agrees to buy the entire remaining balance of $${agreementData.balance || "________________"} and shall pay to SELLER $${agreementData.brokerOfferToSeller || "________________"} for the full purchase of said ${agreementData.paperType || "________________"} with payments beginning to PURCHASER on ${agreementData.firstPaymentDate || "________________"}`,
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "3. COSTS: PURCHASER will pay title and appraisal costs",
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "4. SELLER warrants that all real property taxes that are due and payable have been paid.",
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "5. CONTINGENT: This purchase shall be expressly contingent upon the following Conditions:",
          size: 20
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `A. The real property securing the above described ${agreementData.paperType || "________________"} shall be of sufficient value to secure the investment. The determination of sufficient value shall be the sole discretion of PURCHASER.`,
          size: 20
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `B. PURCHASER shall have 30 days to fully examine the SELLER'S title, the ${agreementData.paperType || "________________"}, the CONTRACTED property, payment history, credit and financial status of the payers.`,
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "6. If any of the above mentioned conditions of sale are found to be unacceptable to PURCHASER, the PURCHASER shall notify SELLER and this contract shall become null and void.",
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `7. SELLER shall keep and maintain documentation of any ${agreementData.paperType || "________________"} payments that are paid during the processing of this transaction.`,
          size: 20
        })
      ],
      spacing: { after: 150 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "8. This agreement shall be in full force and effect for a period of 30 days from the date of the PURCHASER receiving all of the documentation requested.",
          size: 20
        })
      ],
      spacing: { after: 400 }
    }),
    
    // Signature lines
    new Paragraph({
      children: [
        new TextRun({
          text: "______________________________________                    ______________________________________",
          size: 20
        })
      ],
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `${agreementData.primarySellerName || "Primary Seller"}                                        Contract Funding Group, LLC`,
          size: 20
        })
      ],
      spacing: { after: 200 }
    }),
    ...(agreementData.secondarySellerName ? [
      new Paragraph({
        children: [
          new TextRun({
            text: "______________________________________",
            size: 20
          })
        ],
        spacing: { after: 100 }
      }),
      new Paragraph({
        children: [
          new TextRun({
            text: agreementData.secondarySellerName,
            size: 20
          })
        ],
        spacing: { after: 200 }
      })
    ] : [])
  ];
};